import { useEffect, useId, useRef, useState, type DragEvent } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import { RecordLink } from '../../components/RecordLink'
import { apiErrorMessage, apiErrorSubject } from '../../lib/errors'
import { useAuth } from '../auth/context'
import { sessionOwner } from '../auth/token'
import { uploadRateConfirmation, type RateConfirmationDetail } from './api'

/** How one file of a drop ended: filed and reading, or refused with the server's reason. */
type Outcome =
  | { fileName: string; record: RateConfirmationDetail }
  | { fileName: string; error: unknown }

/**
 * Where a rate confirmation enters the platform (3.1): dropped on the box, or chosen with the
 * button — the button is the way in for a keyboard, and for a phone, which has nothing to drag.
 * Several files go up one after the other, and each says how it went: a refusal names its file
 * and its reason, and a file already on file links to the record that holds it. The reading
 * happens on the server, after the answer; the list shows it arrive.
 *
 * <p>
 * Nothing is judged here: whether a file is a PDF is the server's question, asked of its first
 * bytes, and its answer is translated — a check of the name here would be a second rule that
 * a renamed file gets past.
 * </p>
 */
export function UploadBox({ onUploaded }: { onUploaded: () => void }) {
  const { t } = useTranslation()
  const userId = useAuth().user?.id ?? null
  const inputId = useId()
  const input = useRef<HTMLInputElement>(null)
  const mounted = useRef(true)
  const [dragging, setDragging] = useState(false)
  const [uploading, setUploading] = useState<string | null>(null)
  const [outcomes, setOutcomes] = useState<Outcome[]>([])

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // Another account on this tab: what the last one uploaded is not theirs to read.
  useEffect(() => {
    setOutcomes([])
  }, [userId])

  /**
   * A batch belongs to the account that started it and to the box that shows it. The owner is
   * asked of the token itself — another tab's sign-in can hand this tab a different account
   * while a file goes up, before React has heard of it — once each file answers, before its
   * outcome is shown. One look is enough: nothing waits between it and the next file's
   * request, which reads the token in the same breath. A batch whose account changed, or
   * whose box was closed, stops: the files it had sent are on file under the account that
   * sent them; the rest never go (Copilot on #66).
   */
  async function upload(files: readonly File[]) {
    if (files.length === 0 || uploading !== null) {
      return
    }

    const owner = sessionOwner()
    const stillTheirs = () => mounted.current && sessionOwner() === owner

    setOutcomes([])
    const done: Outcome[] = []

    for (const file of files) {
      setUploading(file.name)
      let outcome: Outcome

      try {
        outcome = { fileName: file.name, record: await uploadRateConfirmation(file) }
      } catch (caught) {
        outcome = { fileName: file.name, error: caught }
      }

      if (!stillTheirs()) {
        break
      }

      done.push(outcome)
      setOutcomes([...done])
      if ('record' in outcome) {
        onUploaded()
      }
    }

    if (!mounted.current) {
      return
    }

    setUploading(null)

    // The same file chosen again after a refusal must still fire a change.
    if (input.current !== null) {
      input.current.value = ''
    }
  }

  function drop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault()
    setDragging(false)
    void upload([...event.dataTransfer.files])
  }

  return (
    <section className="rc-upload" aria-labelledby={`${inputId}-title`}>
      <div
        className={dragging ? 'rc-upload__zone rc-upload__zone--over' : 'rc-upload__zone'}
        onDragEnter={(event) => {
          event.preventDefault()
          setDragging(true)
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          // Leaving for one of the box's own children is not leaving the box.
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setDragging(false)
          }
        }}
        onDrop={drop}
      >
        <h2 id={`${inputId}-title`} className="rc-upload__title">
          {t('rateConfirmations.upload.title')}
        </h2>
        <p className="rc-upload__hint">{t('rateConfirmations.upload.hint')}</p>
        {/* The input keeps the focus and the label draws it: the stylesheet rings the label
            while the input before it is focused, so a keyboard sees where it is. */}
        <input
          ref={input}
          id={inputId}
          className="visually-hidden rc-upload__input"
          type="file"
          accept="application/pdf,.pdf"
          multiple
          disabled={uploading !== null}
          onChange={(event) => void upload([...(event.target.files ?? [])])}
        />
        <label htmlFor={inputId} className="button rc-upload__choose">
          {t('rateConfirmations.upload.choose')}
        </label>
        <p className="rc-upload__status" role="status">
          {uploading === null ? '' : t('rateConfirmations.upload.uploading', { file: uploading })}
        </p>
      </div>

      {outcomes.length > 0 && (
        <ul className="rc-upload__outcomes">
          {outcomes.map((outcome, index) =>
            'record' in outcome ? (
              <li key={`${index}-${outcome.fileName}`}>
                {t('rateConfirmations.upload.uploaded', { file: outcome.fileName })}{' '}
                <Link to={`/rate-confirmations/${outcome.record.id}`}>{t('rateConfirmations.upload.open')}</Link>
              </li>
            ) : (
              <li key={`${index}-${outcome.fileName}`} className="alert" role="alert">
                <strong>{outcome.fileName}</strong>: {apiErrorMessage(outcome.error, t, 'rateConfirmations.upload.failed')}{' '}
                <Refused error={outcome.error} />
              </li>
            ),
          )}
        </ul>
      )}
    </section>
  )
}

/** The record a refusal names — the rate confirmation already holding the same file. */
function Refused({ error }: { error: unknown }) {
  const subject = apiErrorSubject(error)
  return subject === null ? null : <RecordLink subject={subject} />
}
